const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api";
const TOKEN_KEY = "scp_access_token";

let accessToken = localStorage.getItem(TOKEN_KEY);
let refreshPromise: Promise<string | null> | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function getAccessToken() {
  return accessToken;
}

async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;
  refreshPromise = fetch(`${API_BASE_URL}/auth/refresh`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
  })
    .then(async (response) => {
      if (!response.ok) return null;
      const payload = (await response.json()) as { accessToken: string };
      setAccessToken(payload.accessToken);
      return payload.accessToken;
    })
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

async function parseError(response: Response) {
  const payload = (await response.json().catch(() => null)) as {
    message?: string | string[];
    error?: string;
  } | null;
  const message = Array.isArray(payload?.message)
    ? payload.message.join(". ")
    : payload?.message;
  return message || payload?.error || `Error ${response.status}`;
}

export async function api<T>(
  path: string,
  options: RequestInit & { skipAuth?: boolean; retry?: boolean } = {},
): Promise<T> {
  const { skipAuth, retry = true, ...requestOptions } = options;
  const headers = new Headers(requestOptions.headers);
  if (
    !(requestOptions.body instanceof FormData) &&
    requestOptions.body &&
    !headers.has("content-type")
  ) {
    headers.set("content-type", "application/json");
  }
  if (!skipAuth && accessToken)
    headers.set("authorization", `Bearer ${accessToken}`);
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...requestOptions,
    headers,
    credentials: "include",
  });
  if (response.status === 401 && !skipAuth && retry) {
    const token = await refreshAccessToken();
    if (token) return api<T>(path, { ...options, retry: false });
    setAccessToken(null);
  }
  if (!response.ok) throw new Error(await parseError(response));
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

/* descarga binaria (PDF, archivos); admite POST con cuerpo para vistas previas */
export async function apiBlob(path: string, init: RequestInit = {}) {
  const headers = new Headers(init.headers);
  if (accessToken) headers.set("authorization", `Bearer ${accessToken}`);
  let response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers,
    credentials: "include",
  });
  if (response.status === 401 && (await refreshAccessToken())) {
    headers.set("authorization", `Bearer ${accessToken}`);
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers,
      credentials: "include",
    });
  }
  if (!response.ok) throw new Error(await parseError(response));
  return response.blob();
}

/*
 * Los archivos (videos, PDF, imagenes) llegan como rutas relativas a la API
 * (/api/storage/...). Si la API vive en otro origen, como en desarrollo, hay
 * que anteponerlo; en produccion la ruta relativa ya apunta al mismo puerto.
 */
export function resolveApiUrl(url: string) {
  if (!url.startsWith("/api/")) return url;
  return `${API_BASE_URL.replace(/\/api\/?$/, "")}${url}`;
}

export { API_BASE_URL };
